import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { plural } from "@/helpers/CommonHelpers";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import {
  ConflictResolutionActionColors,
  ConflictResolutionActionLabels,
} from "@/models/ConflictResolutionAction";
import { ConflictRuleActionSummary, ConflictRuleGroup, ConflictRuleSkipSummary } from "@/models/ConflictRule";
import RuleGroupList from "./RuleGroupList";

const Tile: React.FC<{ label: string; value: number; className?: string }> = ({ label, value, className }) => (
  <div className="flex flex-col gap-0.5 rounded-md border bg-card px-3 py-2">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={cn("text-lg font-semibold tabular-nums", className)}>{value.toLocaleString()}</span>
  </div>
);

/** One line of a breakdown: a label, its count, and its sample groups behind a toggle when there are any. */
const BreakdownRow: React.FC<{
  label: React.ReactNode;
  count: string;
  samples: ConflictRuleGroup[] | null | undefined;
}> = ({ label, count, samples }) => {
  const [open, setOpen] = useState(false);
  const hasSamples = (samples?.length ?? 0) > 0;
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        className={cn(
          "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm",
          hasSamples ? "hover:bg-muted/60" : "cursor-default"
        )}
        onClick={() => hasSamples && setOpen(!open)}
        aria-expanded={hasSamples ? open : undefined}
        disabled={!hasSamples}
      >
        <ChevronRight
          className={cn(
            "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90",
            !hasSamples && "invisible"
          )}
        />
        <span className="min-w-0 flex-1">{label}</span>
        <span className="shrink-0 tabular-nums text-muted-foreground">{count}</span>
      </button>
      {open && samples && (
        <div className="pl-6">
          <p className="mb-2 text-xs text-muted-foreground">
            {samples.length === 1 ? "One example:" : `${samples.length} examples:`}
          </p>
          <RuleGroupList groups={samples} />
        </div>
      )}
    </div>
  );
};

interface RuleOutcomeSummaryProps {
  /** A preview says what the rule would do; a run says what it did. */
  tense: "preview" | "run";
  groupsDecided: number;
  filesDecided: number;
  copiesThatWait: number;
  groupsSkipped: number;
  actions: ConflictRuleActionSummary[];
  skipped: ConflictRuleSkipSummary[];
}

/**
 * The counts of a rule's preview or run: groups and files decided, the renamed and accepting copies held for their
 * winner, and every skipped group by the check that stopped it. A preview's lines open its sample groups; a run
 * carries none.
 */
const RuleOutcomeSummary: React.FC<RuleOutcomeSummaryProps> = ({
  tense,
  groupsDecided,
  filesDecided,
  copiesThatWait,
  groupsSkipped,
  actions,
  skipped,
}) => {
  const preview = tense === "preview";
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label={preview ? "Groups to decide" : "Groups decided"} value={groupsDecided} />
        <Tile label={preview ? "Files to decide" : "Files decided"} value={filesDecided} />
        <Tile label={preview ? "Copies that will wait" : "Copies set to wait"} value={copiesThatWait} />
        <Tile
          label="Groups skipped"
          value={groupsSkipped}
          className={groupsSkipped > 0 ? "text-amber-600 dark:text-amber-400" : undefined}
        />
      </div>

      <div className="flex flex-col gap-1">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {preview ? "To decide" : "Decided"}
        </h4>
        {actions.length === 0 ? (
          <p className="px-2 text-sm text-muted-foreground">
            {preview ? "The rule decides no group now." : "The run decided no group."}
          </p>
        ) : (
          actions.map((summary) => (
            <BreakdownRow
              key={summary.action}
              label={
                <span
                  className={cn("font-medium", colorVariantClasses[ConflictResolutionActionColors[summary.action]]?.text)}
                >
                  {ConflictResolutionActionLabels[summary.action]}
                </span>
              }
              count={plural(summary.fileCount, "file")}
              samples={summary.samples}
            />
          ))
        )}
      </div>

      <div className="flex flex-col gap-1">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Skipped</h4>
        {skipped.length === 0 ? (
          <p className="px-2 text-sm text-muted-foreground">No group is skipped.</p>
        ) : (
          skipped.map((summary) => (
            <BreakdownRow
              key={summary.check}
              label={summary.label}
              count={plural(summary.groupCount, "group")}
              samples={summary.samples}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default RuleOutcomeSummary;
